import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { grey } from '@mui/material/colors';
import Button from '../../../../components/Button/Button';
import Grid from '../../../../components/Grid/Grid';
import Dialog from '../../../../components/Dialog/Dialog';
import DialogTitle from '../../../../components/DialogTitle/DialogTitle';
import DialogContent from '../../../../components/DialogContent/DialogContent';
import useForm from '../../../../hooks/useForm';
import CreatePilotTransferForm from './CreatePilotTransferForm';
import CreatePilotTransferConfirmForm from './CreatePilotTransferConfirmForm';
import useCancelToken from '../../../../hooks/useCancelToken';
import { createPilotTransfer } from '../../../../services/transfer-update';
import CreatePilotTransferSuccess from './CreatePilotTransferSuccess';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';
import CreatePilotTransferError from './CreatePilotTransferError';

const CreatingStep = {
  FILL_FORM: 'FILL_FORM',
  CONFIRM_FORM: 'CONFIRM_FORM',
  CREATE_PILOT_TRANSFER_SUCCESS: 'CREATE_PILOT_TRANSFER',
  CREATE_PILOT_TRANSFER_ERROR: 'CREATE_PILOT_TRANSFER_ERROR',
};

const CreatingStepTitle = {
  [CreatingStep.FILL_FORM]: '建立試燒單 / Membuat tes pembakaran',
  [CreatingStep.CONFIRM_FORM]: '確認建立試燒單？',
  [CreatingStep.CREATE_PILOT_TRANSFER_SUCCESS]: '建立試燒單成功 / Berhasil membuat tes pembakaran',
  [CreatingStep.CREATE_PILOT_TRANSFER_ERROR]: '建立試燒單失敗 / Gagal membuat tes pembakaran',
};

const CreatePilotTransferDialog = ({ transInfo, machineList }) => {
  const { newCancelToken } = useCancelToken();
  const [isOpen, setIsOpen] = useState(false);
  const [creatingStep, setCreatingStep] = useState(CreatingStep.FILL_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pilotTransferId, setPilotTransferId] = useState('');
  const initialForm = {
    bucketNo: '',
    originalMaterialName: transInfo.materialName,
    materialName: transInfo.materialName,
    materialId: '',
    pilotTransferQty: '',
    machineId: transInfo.machineId,
    machineName: machineList.find((item) => item.value === transInfo.machineId)?.name,
    workId: transInfo.id,
    isPQCPilot: false,
    elNo: transInfo.elNo,
    transId: transInfo.transId,
    comment: '',
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);

  const handleOpenPilotTransfer = () => {
    setIsOpen(true);
    resetFormErr();
    setCreatingStep(CreatingStep.FILL_FORM);
  };

  const handleClosePilotTransfer = () => {
    setIsOpen(false);
    resetFormErr();
    setFormData(initialForm);
    setCreatingStep(CreatingStep.FILL_FORM);
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const { bucketNo, materialId, pilotTransferQty, machineId } = formData;
    resetFormErr();
    // 檢查 bucketNo 是否是 4 或 5 位數字
    if (!/^\d{4,5}$/.test(bucketNo)) {
      setFormErr((prev) => ({ ...prev, bucketNo: true }));
      return false;
    }
    if (!materialId) {
      setFormErr((prev) => ({ ...prev, materialId: true }));
      return false;
    }
    if (!pilotTransferQty) {
      setFormErr((prev) => ({ ...prev, pilotTransferQty: true }));
      return false;
    }
    if (!machineId) {
      setFormErr((prev) => ({ ...prev, machineId: true }));
      return false;
    }
    return true;
  };

  const handleCancelConfirmClick = () => {
    setCreatingStep(CreatingStep.FILL_FORM);
  };

  const handleCreatePilotTransfer = async () => {
    setIsCreating(true);
    const cancelToken = newCancelToken();
    await createPilotTransfer({
      data: formData,
      cancelToken,
      onSuccess: (result) => {
        setCreatingStep(CreatingStep.CREATE_PILOT_TRANSFER_SUCCESS);
        setPilotTransferId(result.pilotTransferIdWithPrefix);
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        setErrorMessage(
          noPrivilegeMsg ||
            `建立試燒單失敗！請確認網路是否正常或聯繫軟體部門：${result} / Gagal membuat tes pembakaran.`
        );
        setCreatingStep(CreatingStep.CREATE_PILOT_TRANSFER_ERROR);
      },
      onSettled: () => {
        setIsCreating(false);
      },
    });
  };

  const handleCreatePilotTransferClick = () => {
    if (!validateForm()) return;
    if (formData.materialName === formData.originalMaterialName) {
      handleCreatePilotTransfer();
      return;
    }
    setCreatingStep(CreatingStep.CONFIRM_FORM);
  };

  const handleCreatePilotTransferSuccessClick = () => {
    handleClosePilotTransfer();
    window.location.href = `${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_PILOT_BASE_PATH}/trans-update/forming/${pilotTransferId}`;
  };

  const handleCreatePilotTransferErrorClick = () => {
    setCreatingStep(CreatingStep.FILL_FORM);
    setErrorMessage('');
    setPilotTransferId('');
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      originalMaterialName: transInfo.materialName,
      materialName: transInfo.materialName,
      machineId: transInfo.machineId,
      machineName: machineList.find((item) => item.value === transInfo.machineId)?.name,
      workId: transInfo.id,
      elNo: transInfo.elNo,
      transId: transInfo.transId,
    }));
  }, [transInfo]);

  return (
    <Grid sx={{ marginBottom: 2, pr: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        onClick={handleOpenPilotTransfer}
        fullWidth
        sx={{
          py: 1.5,
          bgcolor: 'background.paper',
          borderColor: 'primary.main',
        }}
      >
        建立試燒單 / Membuat tes pembakaran
      </Button>
      <Dialog open={isOpen} onClose={handleClosePilotTransfer} fullWidth maxWidth="md">
        <DialogTitle>{CreatingStepTitle[creatingStep]}</DialogTitle>
        <DialogContent>
          {creatingStep === CreatingStep.FILL_FORM && (
            <CreatePilotTransferForm
              formData={formData}
              handleFormChange={handleFormChange}
              formErr={formErr}
              machineList={machineList}
              setFormData={setFormData}
              disabled={isCreating}
            />
          )}
          {creatingStep === CreatingStep.CONFIRM_FORM && (
            <CreatePilotTransferConfirmForm formData={formData} />
          )}
          {creatingStep === CreatingStep.CREATE_PILOT_TRANSFER_SUCCESS && (
            <CreatePilotTransferSuccess pilotTransferId={pilotTransferId} />
          )}
          {creatingStep === CreatingStep.CREATE_PILOT_TRANSFER_ERROR && (
            <CreatePilotTransferError errorMessage={errorMessage} />
          )}
        </DialogContent>
        <Grid container gap={1} justifyContent="flex-end" sx={{ px: 2.5, pt: 0, pb: 2 }}>
          {creatingStep === CreatingStep.FILL_FORM && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreatePilotTransferClick}
              sx={{ fontWeight: 500 }}
              disabled={isCreating}
            >
              {isCreating ? (
                <CircularProgress
                  sx={{
                    '& .MuiCircularProgress-root': {
                      width: '24px !important',
                      height: '24px !important',
                      color: 'white',
                    },
                  }}
                />
              ) : (
                '建立'
              )}
            </Button>
          )}
          {creatingStep === CreatingStep.CONFIRM_FORM && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCancelConfirmClick}
                sx={{ fontWeight: 500, bgcolor: grey[400], color: 'white' }}
                disabled={isCreating}
              >
                取消
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreatePilotTransfer}
                sx={{ fontWeight: 500 }}
                disabled={isCreating}
              >
                {isCreating ? (
                  <CircularProgress
                    sx={{
                      '& .MuiCircularProgress-root': {
                        width: '20px !important',
                        height: '20px !important',
                        color: 'white',
                      },
                    }}
                  />
                ) : (
                  '確認建立'
                )}
              </Button>
            </>
          )}
          {creatingStep === CreatingStep.CREATE_PILOT_TRANSFER_SUCCESS && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreatePilotTransferSuccessClick}
              sx={{ fontWeight: 500 }}
            >
              關閉
            </Button>
          )}
          {creatingStep === CreatingStep.CREATE_PILOT_TRANSFER_ERROR && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreatePilotTransferErrorClick}
              sx={{ fontWeight: 500 }}
            >
              返回
            </Button>
          )}
        </Grid>
      </Dialog>
    </Grid>
  );
};

CreatePilotTransferDialog.propTypes = {
  transInfo: PropTypes.shape({
    transId: PropTypes.string.isRequired,
    elNo: PropTypes.string.isRequired,
    materialName: PropTypes.string.isRequired,
    machineId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
  machineList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default CreatePilotTransferDialog;
